import { create } from 'zustand'
import { Node, Edge } from 'reactflow'

export interface WorkflowData {
  id?: string
  name: string
  nodes: Node[]
  edges: Edge[]
  createdAt: Date
  updatedAt: Date
  status?: 'draft' | 'running' | 'completed' | 'failed'
}

export interface ExecutionLog {
  id: string
  timestamp: Date
  level: 'info' | 'warning' | 'error' | 'debug'
  message: string
  nodeId?: string
  data?: any
}

export interface WorkflowExecution {
  id: string
  workflowId: string
  status: 'running' | 'paused' | 'completed' | 'failed' | 'cancelled'
  startTime: Date
  endTime?: Date
  logs: ExecutionLog[]
  results?: any
  progress: number
}

interface WorkflowStore {
  // Current workflow state
  currentWorkflow: WorkflowData | null
  workflows: WorkflowData[]
  
  // Execution state
  currentExecution: WorkflowExecution | null
  isExecuting: boolean
  executionLogs: ExecutionLog[]
  
  // Actions
  setCurrentWorkflow: (workflow: WorkflowData | null) => void
  saveWorkflow: (workflow: Omit<WorkflowData, 'id'>) => Promise<string>
  loadWorkflow: (id: string) => Promise<WorkflowData | null>
  deleteWorkflow: (id: string) => Promise<void>
  
  // Execution actions
  executeWorkflow: (workflow: Omit<WorkflowData, 'id'>) => Promise<string>
  pauseExecution: (executionId: string) => Promise<void>
  resumeExecution: (executionId: string) => Promise<void>
  stopExecution: (executionId: string) => Promise<void>
  
  // Log management
  addLog: (log: Omit<ExecutionLog, 'id' | 'timestamp'>) => void
  clearLogs: () => void
  
  // WebSocket connection for real-time updates
  connectToExecution: (executionId: string) => void
  disconnectFromExecution: () => void
  
  // Mock execution simulation
  simulateExecution: (executionId: string) => void
}

// Mock API functions - replace with actual API calls
const mockApi = {
  async saveWorkflow(workflow: Omit<WorkflowData, 'id'>): Promise<string> {
    const id = `workflow-${Date.now()}`
    const savedWorkflow = { ...workflow, id }
    
    // Save to localStorage for now
    const workflows = JSON.parse(localStorage.getItem('flowran-workflows') || '[]')
    workflows.push(savedWorkflow)
    localStorage.setItem('flowran-workflows', JSON.stringify(workflows))
    
    return id
  },
  
  async loadWorkflow(id: string): Promise<WorkflowData | null> {
    const workflows = JSON.parse(localStorage.getItem('flowran-workflows') || '[]')
    return workflows.find((w: WorkflowData) => w.id === id) || null
  },
  
  async deleteWorkflow(id: string): Promise<void> {
    const workflows = JSON.parse(localStorage.getItem('flowran-workflows') || '[]')
    const filtered = workflows.filter((w: WorkflowData) => w.id !== id)
    localStorage.setItem('flowran-workflows', JSON.stringify(filtered))
  },
  
  async executeWorkflow(workflow: Omit<WorkflowData, 'id'>): Promise<string> {
    const executionId = `execution-${Date.now()}`
    
    // In a real app, this would send the workflow to the backend
    console.log('Executing workflow:', workflow)
    
    return executionId
  },
  
  async pauseExecution(executionId: string): Promise<void> {
    console.log('Pausing execution:', executionId)
  },
  
  async resumeExecution(executionId: string): Promise<void> {
    console.log('Resuming execution:', executionId)
  },
  
  async stopExecution(executionId: string): Promise<void> {
    console.log('Stopping execution:', executionId)
  }
}

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  // Initial state
  currentWorkflow: null,
  workflows: [],
  currentExecution: null,
  isExecuting: false,
  executionLogs: [],
  
  // Workflow management
  setCurrentWorkflow: (workflow) => {
    set({ currentWorkflow: workflow })
  },
  
  saveWorkflow: async (workflow) => {
    try {
      const id = await mockApi.saveWorkflow(workflow)
      const savedWorkflow = { ...workflow, id }
      
      set((state) => ({
        workflows: [...state.workflows.filter(w => w.id !== id), savedWorkflow],
        currentWorkflow: savedWorkflow
      }))
      
      return id
    } catch (error) {
      console.error('Failed to save workflow:', error)
      throw error
    }
  },
  
  loadWorkflow: async (id) => {
    try {
      const workflow = await mockApi.loadWorkflow(id)
      if (workflow) {
        set({ currentWorkflow: workflow })
      }
      return workflow
    } catch (error) {
      console.error('Failed to load workflow:', error)
      throw error
    }
  },
  
  deleteWorkflow: async (id) => {
    try {
      await mockApi.deleteWorkflow(id)
      set((state) => ({
        workflows: state.workflows.filter(w => w.id !== id),
        currentWorkflow: state.currentWorkflow?.id === id ? null : state.currentWorkflow
      }))
    } catch (error) {
      console.error('Failed to delete workflow:', error)
      throw error
    }
  },
  
  // Execution management
  executeWorkflow: async (workflow) => {
    try {
      set({ isExecuting: true, executionLogs: [] })
      
      const executionId = await mockApi.executeWorkflow(workflow)
      
      const execution: WorkflowExecution = {
        id: executionId,
        workflowId: workflow.name,
        status: 'running',
        startTime: new Date(),
        logs: [],
        progress: 0
      }
      
      set({ currentExecution: execution })
      
      // Start mock execution simulation
      get().simulateExecution(executionId)
      
      return executionId
    } catch (error) {
      set({ isExecuting: false })
      console.error('Failed to execute workflow:', error)
      throw error
    }
  },
  
  pauseExecution: async (executionId) => {
    try {
      await mockApi.pauseExecution(executionId)
      set((state) => ({
        currentExecution: state.currentExecution 
          ? { ...state.currentExecution, status: 'paused' }
          : null
      }))
    } catch (error) {
      console.error('Failed to pause execution:', error)
      throw error
    }
  },
  
  resumeExecution: async (executionId) => {
    try {
      await mockApi.resumeExecution(executionId)
      set((state) => ({
        currentExecution: state.currentExecution 
          ? { ...state.currentExecution, status: 'running' }
          : null
      }))
    } catch (error) {
      console.error('Failed to resume execution:', error)
      throw error
    }
  },
  
  stopExecution: async (executionId) => {
    try {
      await mockApi.stopExecution(executionId)
      set((state) => ({
        currentExecution: state.currentExecution 
          ? { ...state.currentExecution, status: 'cancelled', endTime: new Date() }
          : null,
        isExecuting: false
      }))
    } catch (error) {
      console.error('Failed to stop execution:', error)
      throw error
    }
  },
  
  // Log management
  addLog: (log) => {
    const newLog: ExecutionLog = {
      ...log,
      id: `log-${Date.now()}-${Math.random()}`,
      timestamp: new Date()
    }
    
    set((state) => ({
      executionLogs: [...state.executionLogs, newLog]
    }))
  },
  
  clearLogs: () => {
    set({ executionLogs: [] })
  },
  
  // WebSocket connection (mock implementation)
  connectToExecution: (executionId) => {
    console.log('Connecting to execution:', executionId)
    // In a real app, establish WebSocket connection here
  },
  
  disconnectFromExecution: () => {
    console.log('Disconnecting from execution')
    // In a real app, close WebSocket connection here
  },
  
  // Mock execution simulation
  simulateExecution: (_executionId: string) => {
    const messages = [
      { level: 'info' as const, message: 'Initializing CrewAI workflow execution' },
      { level: 'info' as const, message: 'Loading agents and tasks configuration' },
      { level: 'info' as const, message: 'Starting Research Agent...' },
      { level: 'debug' as const, message: 'Connecting to external APIs...' },
      { level: 'info' as const, message: 'Research Agent gathering data...' },
      { level: 'info' as const, message: 'Processing 247 data points...' },
      { level: 'info' as const, message: 'Research Agent completed successfully' },
      { level: 'info' as const, message: 'Starting Writer Agent...' },
      { level: 'info' as const, message: 'Generating content structure...' },
      { level: 'info' as const, message: 'Writing executive summary...' },
      { level: 'debug' as const, message: 'Optimizing content for readability...' },
      { level: 'info' as const, message: 'Writer Agent completed successfully' },
      { level: 'info' as const, message: 'Starting Analysis Task...' },
      { level: 'info' as const, message: 'Analyzing results and generating insights...' },
      { level: 'info' as const, message: 'Workflow execution completed successfully' }
    ]
    
    let messageIndex = 0
    const interval = setInterval(() => {
      if (messageIndex < messages.length) {
        const message = messages[messageIndex]
        get().addLog(message)
        
        // Update progress
        const progress = ((messageIndex + 1) / messages.length) * 100
        set((state) => ({
          currentExecution: state.currentExecution 
            ? { ...state.currentExecution, progress }
            : null
        }))
        
        messageIndex++
      } else {
        // Execution completed
        set((state) => ({
          currentExecution: state.currentExecution 
            ? { 
                ...state.currentExecution, 
                status: 'completed', 
                endTime: new Date(),
                progress: 100
              }
            : null,
          isExecuting: false
        }))
        clearInterval(interval)
      }
    }, 2000) // Add a new log every 2 seconds
  }
}))

